import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  IconButton,
  Collapse,
  Stack,
  Tooltip,
  Fab,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import {
  WarningAmberRounded as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MonitorHeart as VitalsIcon,
  EditNote as NoteIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  ChecklistRtl as ChecklistIcon,
} from '@mui/icons-material';
import { complianceViolations } from '../BaseURL';

const PRIMARY        = '#41a386';
const WARN           = '#b45309';
const WARN_BG        = 'rgba(234,179,8,0.10)';
const WARN_BORDER    = 'rgba(234,179,8,0.35)';
const DANGER         = '#b91c1c';
const DANGER_BG      = 'rgba(220,38,38,0.10)';
const BORDER         = '#e5e9ef';
const MUTED          = '#64748b';
const STRONG         = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem',
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
};

/**
 * Compliance Alerts — Floating Action Button + Dialog. Lists every active
 * admission whose admission has any day below the required vitals/notes
 * minimums. Clicking a row's "View vitals" or "View notes" button hands the
 * patient off to the host page's `onSelectPatient` so the nurse lands in the
 * full patient workspace (vitals, notes, medications, everything).
 *
 * Props:
 *   scope          'ward' | 'emergency' | 'nursing'  filters the patient set
 *   wardIds        comma-separated string of ward ids
 *   onSelectPatient(row, tab) REQUIRED — invoked when the nurse clicks View
 *                  vitals (tab='vitals') or View notes (tab='notes'). The
 *                  host page is responsible for opening that patient.
 *                  On dashboard pages (no inpatient workspace mounted), the
 *                  callback typically writes a session handoff and switches
 *                  the sidebar to the InPatient section.
 *   refreshKey     bump this number to force a reload
 */
const ComplianceAlerts = ({ scope = 'ward', wardIds = '', onSelectPatient, refreshKey = 0 }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows]       = useState([]);
  const [expanded, setExpanded] = useState({});
  const [minVitalsPerDay, setMinVitalsPerDay] = useState(3);
  const [minNotesPerDay,  setMinNotesPerDay]  = useState(1);
  const [open, setOpen] = useState(false);

  const inFlightRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const { data } = await axios.get(complianceViolations, {
        params: { scope, wardIds },
        timeout: 12000,
      });
      if (data?.success) {
        setRows(data.results || []);
        setMinVitalsPerDay(data.minVitalsPerDay ?? data.minPerDay ?? 3);
        setMinNotesPerDay( data.minNotesPerDay  ?? 1);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error('Compliance alerts load failed:', err.message || err);
      setRows([]);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [scope, wardIds]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handlePick = (row, tab) => {
    if (typeof onSelectPatient !== 'function') {
      console.warn('ComplianceAlerts: onSelectPatient not provided — patient handoff skipped.');
      return;
    }
    onSelectPatient(row, tab);
    setOpen(false);
  };

  const count = rows.length;
  const hasAny = count > 0;

  const fab = (
    <Tooltip
      title={hasAny ? `${count} admission${count === 1 ? '' : 's'} below compliance` : 'All active admissions compliant'}
      arrow
      placement="left"
    >
      <Badge
        badgeContent={hasAny ? count : 0}
        color="error"
        overlap="circular"
        sx={{
          position: 'fixed',
          bottom: 28,
          right: 28,
          zIndex: 1300,
          '& .MuiBadge-badge': { fontWeight: 700, fontSize: '0.74rem', minWidth: 22, height: 22 },
        }}
      >
        <Fab
          onClick={() => setOpen(true)}
          aria-label="Open admission compliance"
          sx={{
            bgcolor: hasAny ? WARN_BG : 'rgba(65,163,134,0.12)',
            color:   hasAny ? WARN     : PRIMARY,
            border: `1px solid ${hasAny ? WARN_BORDER : 'rgba(65,163,134,0.35)'}`,
            boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
            '&:hover': {
              bgcolor: hasAny ? 'rgba(234,179,8,0.18)' : 'rgba(65,163,134,0.18)',
            },
          }}
        >
          {hasAny ? <WarningIcon /> : <ChecklistIcon />}
        </Fab>
      </Badge>
    </Tooltip>
  );

  return (
    <>
      {fab}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          px: 2, py: 1.5,
          bgcolor: hasAny ? WARN_BG : 'rgba(65,163,134,0.10)',
          color: hasAny ? WARN : PRIMARY,
          borderBottom: `1px solid ${hasAny ? WARN_BORDER : 'rgba(65,163,134,0.30)'}`,
        }}>
          {hasAny ? <WarningIcon sx={{ fontSize: 22 }} /> : <CheckIcon sx={{ fontSize: 22 }} />}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ ...LABEL_SX, color: 'inherit' }}>
              Admission Compliance
            </Typography>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'inherit', lineHeight: 1.2 }}>
              {hasAny
                ? `${count} admission${count === 1 ? '' : 's'} below compliance`
                : 'All active admissions are compliant'}
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color: 'inherit', opacity: 0.85, mt: 0.25 }}>
              Minimum {minVitalsPerDay} vitals & {minNotesPerDay} note{minNotesPerDay === 1 ? '' : 's'} per admission day.
            </Typography>
          </Box>
          <Tooltip title="Refresh" arrow>
            <IconButton size="small" onClick={fetchData} disabled={loading} sx={{ color: 'inherit' }}>
              {loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {!hasAny && !loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckIcon sx={{ fontSize: 36, color: PRIMARY, mb: 1 }} />
              <Typography sx={{ fontWeight: 700, color: STRONG, fontSize: '0.95rem' }}>
                Nothing to follow up.
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.5 }}>
                Every active admission has its required vitals and nursing notes recorded.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {rows.map(row => {
                const isOpen = !!expanded[row.id];
                return (
                  <Paper
                    key={row.id}
                    variant="outlined"
                    sx={{ borderColor: BORDER, bgcolor: '#fff', overflow: 'hidden' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: STRONG }}>
                          {row.fullName}
                          <Typography component="span" sx={{ ml: 1, fontSize: '0.74rem', color: MUTED, fontWeight: 500 }}>
                            #{row.id}
                          </Typography>
                        </Typography>
                        <Stack direction="row" spacing={1.25} sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}>
                          {row.wardName && (
                            <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                              Ward: <Typography component="span" sx={{ fontSize: '0.72rem', color: STRONG, fontWeight: 600 }}>
                                {row.wardName}{row.bedNumber ? ` · Bed ${row.bedNumber}` : ''}
                              </Typography>
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                            Admitted: <Typography component="span" sx={{ fontSize: '0.72rem', color: STRONG, fontWeight: 600 }}>{row.admissionDate}</Typography>
                          </Typography>
                        </Stack>
                      </Box>

                      <Chip
                        size="small"
                        label={`${row.totalIncomplete} incomplete day${row.totalIncomplete === 1 ? '' : 's'}`}
                        sx={{
                          height: 22, fontSize: '0.72rem', fontWeight: 700,
                          bgcolor: DANGER_BG, color: DANGER, border: `1px solid rgba(220,38,38,0.3)`,
                        }}
                      />

                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        <Tooltip title="Open this patient's workspace at the Vitals tab" arrow>
                          <Button
                            size="small"
                            startIcon={<VitalsIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handlePick(row, 'vitals')}
                            sx={{
                              textTransform: 'none', fontSize: '0.72rem', fontWeight: 700,
                              px: 1.25, py: 0.4, borderRadius: 1,
                              bgcolor: PRIMARY, color: '#fff',
                              '&:hover': { bgcolor: '#2f8068' },
                            }}
                          >
                            View vitals
                          </Button>
                        </Tooltip>
                        <Tooltip title="Open this patient's workspace at the Notes tab" arrow>
                          <Button
                            size="small"
                            startIcon={<NoteIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handlePick(row, 'notes')}
                            sx={{
                              textTransform: 'none', fontSize: '0.72rem', fontWeight: 700,
                              px: 1.25, py: 0.4, borderRadius: 1,
                              bgcolor: PRIMARY, color: '#fff',
                              '&:hover': { bgcolor: '#2f8068' },
                            }}
                          >
                            View notes
                          </Button>
                        </Tooltip>
                      </Stack>

                      <IconButton size="small" onClick={() => toggle(row.id)}>
                        {isOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Box>

                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ px: 1.5, pb: 1.25 }}>
                        <Stack spacing={0.5}>
                          {row.incompleteDays.map(day => {
                            const missingItems = [];
                            if (day.missingVitals > 0) missingItems.push(`${day.missingVitals} vital${day.missingVitals === 1 ? '' : 's'}`);
                            if (day.missingNotes  > 0) missingItems.push(`${day.missingNotes} note${day.missingNotes === 1 ? '' : 's'}`);
                            return (
                              <Box
                                key={day.date}
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 1,
                                  px: 1, py: 0.6,
                                  borderRadius: 0.75,
                                  bgcolor: '#f8fafc',
                                  border: `1px solid ${BORDER}`,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: STRONG, minWidth: 90 }}>
                                  {day.date}
                                </Typography>
                                <Chip
                                  size="small"
                                  icon={<VitalsIcon sx={{ fontSize: 12 }} />}
                                  label={`Vitals ${day.vitals}/${day.minVitals ?? minVitalsPerDay}`}
                                  sx={{
                                    height: 20, fontSize: '0.7rem', fontWeight: 600,
                                    bgcolor: day.missingVitals === 0 ? 'rgba(65,163,134,0.12)' : DANGER_BG,
                                    color: day.missingVitals === 0 ? '#2f8068' : DANGER,
                                    border: `1px solid ${day.missingVitals === 0 ? 'rgba(65,163,134,0.3)' : 'rgba(220,38,38,0.3)'}`,
                                    '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                                  }}
                                />
                                <Chip
                                  size="small"
                                  icon={<NoteIcon sx={{ fontSize: 12 }} />}
                                  label={`Notes ${day.notes}/${day.minNotes ?? minNotesPerDay}`}
                                  sx={{
                                    height: 20, fontSize: '0.7rem', fontWeight: 600,
                                    bgcolor: day.missingNotes === 0 ? 'rgba(65,163,134,0.12)' : DANGER_BG,
                                    color: day.missingNotes === 0 ? '#2f8068' : DANGER,
                                    border: `1px solid ${day.missingNotes === 0 ? 'rgba(65,163,134,0.3)' : 'rgba(220,38,38,0.3)'}`,
                                    '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                                  }}
                                />
                                {missingItems.length > 0 && (
                                  <Typography sx={{ fontSize: '0.72rem', color: MUTED, fontStyle: 'italic' }}>
                                    missing: {missingItems.join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ComplianceAlerts;
